// Diálogo y carrusel reutilizables.
// Componente pdp-*: el HTML llega renderizado desde Liquid y este módulo solo añade interacción.

const define = (name, element) => {
  if (!customElements.get(name)) customElements.define(name, element);
};
const lockScroll = (locked) => {
  document.documentElement.style.overflow = locked ? 'hidden' : '';
};

/* ---------- Diálogo (beneficios) ---------- */
class PdpDialog extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.trigger = this.querySelector('[data-open]');
    if (!this.dialog || !this.trigger) return;
    this.trigger.addEventListener('click', () => {
      this.dialog.showModal();
      lockScroll(true);
    });
    this.dialog.querySelector('[data-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    this.dialog.addEventListener('close', () => {
      lockScroll(false);
      this.trigger.focus();
    });
  }
}
define('pdp-dialog', PdpDialog);

/* ---------- Carrusel con desplazamiento nativo ---------- */
class PdpCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.prev?.addEventListener('click', () => this.scrollByPage(-1));
    this.next?.addEventListener('click', () => this.scrollByPage(1));
    this.onScroll = () => requestAnimationFrame(() => this.update());
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.track);
    this.items = [...this.track.children];
    if (this.hasAttribute('data-dots')) this.buildDots();
    this.setupAutoplay();
    this.update();

    // Mouse drag. Loaded apart so that, if it fails, the carousel still works.
    this.track.addEventListener('pointerdown', () => { this.dragStartIndex = this.currentIndex(); });
    import('./pdp-drag.js')
      .then(({ enableDrag }) => enableDrag(this.track, {
        onRelease: (direction) => {
          this.pause?.();
          const target = direction === 0 ? this.currentIndex() : this.dragStartIndex + direction;
          this.goTo(Math.max(0, Math.min(this.items.length - 1, target)));
        },
      }))
      .catch(() => {});
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    clearInterval(this.timer);
  }

  scrollByPage(direction) {
    this.track.scrollBy({ left: direction * this.track.clientWidth * 0.8, behavior: 'smooth' });
  }

  goTo(index) {
    const item = this.items[index];
    if (item) this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
  }

  currentIndex() {
    const left = this.track.scrollLeft;
    let closest = 0;
    this.items.forEach((item, index) => {
      const distance = Math.abs(item.offsetLeft - this.track.offsetLeft - left);
      if (distance < Math.abs(this.items[closest].offsetLeft - this.track.offsetLeft - left)) closest = index;
    });
    return closest;
  }

  buildDots() {
    if (this.items.length < 2) return;
    this.dots = document.createElement('div');
    this.dots.className = 'pdp-carousel__dots';
    this.items.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'pdp-carousel__dot';
      dot.setAttribute('aria-label', `${index + 1} / ${this.items.length}`);
      dot.addEventListener('click', () => { this.pause(); this.goTo(index); });
      this.dots.append(dot);
    });
    this.append(this.dots);
  }

  // Advances on its own every data-autoplay seconds. It stops while the person interacts,
  // while the carousel is off screen, and when the device asks for reduced motion.
  setupAutoplay() {
    const seconds = Number(this.dataset.autoplay || 0);
    if (!seconds || this.items.length < 2 || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.delay = seconds * 1000;
    this.visible = false;
    const start = () => { clearInterval(this.timer); this.timer = setInterval(() => this.advance(), this.delay); };
    this.pause = () => { this.paused = true; clearInterval(this.timer); clearTimeout(this.resume); this.resume = setTimeout(() => { this.paused = false; if (this.visible) start(); }, this.delay * 2); };
    ['pointerdown', 'touchstart', 'wheel', 'focusin'].forEach((type) => this.addEventListener(type, this.pause, { passive: true }));
    this.addEventListener('mouseenter', this.pause);
    this.visibilityObserver = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      if (this.visible && !this.paused) start(); else clearInterval(this.timer);
    }, { threshold: 0.3 });
    this.visibilityObserver.observe(this);
  }

  advance() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 4;
    this.goTo(atEnd ? 0 : this.currentIndex() + 1);
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    this.classList.toggle('is-scrollable', scrollWidth > clientWidth + 4);
    // At the ends the arrow that leads nowhere is hidden, not just greyed out. The start is
    // measured from the first item, because the track padding shifts it a few pixels.
    const firstOffset = this.items?.[0] ? this.items[0].offsetLeft - this.track.offsetLeft : 0;
    if (this.prev) this.prev.hidden = scrollLeft <= Math.max(0, firstOffset) + 4;
    if (this.next) this.next.hidden = scrollLeft + clientWidth >= scrollWidth - 4;
    if (this.dots) {
      const active = scrollLeft + clientWidth >= scrollWidth - 4 ? this.items.length - 1 : this.currentIndex();
      [...this.dots.children].forEach((dot, index) => dot.toggleAttribute('data-active', index === active));
    }
  }
}
define('pdp-carousel', PdpCarousel);
